<template>
    <psi-form-layout-section
        :loading="sectionLoading.instructors"
        title="Program Instructors"
        :icon="$config('icons.instructor')"
        :btn-label="label"
        :btn-icon="$config('icons.instructor')"
        :btn-disabled="disabled"
        @section-save="save"
    >
        <div class="d-flex mb-4 align-center">
            <v-chip color="primary lighten-3 primary--text  text-uppercase"
                >{{ instructors.length }} Instructors</v-chip
            >
            <v-spacer></v-spacer>
            <v-btn-toggle
                v-model="toggle"
                mandatory
                color="primary darken-2"
                ref="btn-toggle"
            >
                <v-btn>
                    Edit Instructor
                    <v-icon class="ml-2">mdi-account-edit</v-icon>
                </v-btn>
                <v-btn>
                    Add Instructors
                    <v-icon class="ml-2">mdi-account-multiple-plus</v-icon>
                </v-btn>
            </v-btn-toggle>
        </div>

        <v-slide-x-transition>
            <psi-form-element
                label="Select Instructor to Edit"
                v-if="isEditing"
            >
                <psi-form-select-field
                    :items="instructors"
                    id="select-instructor"
                    name="instructor"
                    :value="0"
                    item-text="name"
                    item-value="id"
                    placeholder="Select Instructor"
                    return-object
                    @input="selectInstructor"
                >
                </psi-form-select-field>
            </psi-form-element>
        </v-slide-x-transition>
        <v-slide-x-reverse-transition>
            <div>
                <v-tabs
                    v-model="tab"
                    icons-and-text
                    v-if="isAdding && can('instructors.include')"
                >
                    <v-tab href="#tab-invitation">
                        By Invitation
                        <v-icon>{{ $config("icons.email") }}</v-icon>
                    </v-tab>
                    <v-tab href="#tab-program">
                        From Program
                        <v-icon>{{ $config("icons.program") }}</v-icon>
                    </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab" class="mt-4">
                    <v-tab-item key="tab-invitation" value="tab-invitation">
                        <div v-if="instructorsData.length > 0">
                            <fill-mock-names
                                v-if="shouldMock"
                                @fill-names="fillInstructors"
                                :count="instructorCount"
                            ></fill-mock-names>
                            <template
                                v-for="(
                                    { section, fields, modelData }, index
                                ) in instructorsData"
                            >
                                <v-card :key="index" outlined class="mb-4">
                                    <div
                                        class="d-flex justify-end mb-n6 py-0"
                                        v-if="index !== 0"
                                    >
                                        <v-btn
                                            class="my-0 py-0"
                                            text
                                            small
                                            @click.stop="remove(modelData.id)"
                                            ><v-icon small
                                                >mdi-close</v-icon
                                            ></v-btn
                                        >
                                    </div>
                                    <v-card-text>
                                        <psi-form-section
                                            :ref="section.ref"
                                            :value="modelData"
                                            :name="section.name"
                                            label=""
                                            :fields="fields"
                                            color="secondary"
                                            @input="update"
                                        ></psi-form-section>
                                        <v-btn
                                            v-if="isEditing"
                                            color="primary lighten-2"
                                            @click.stop="
                                                inactivateDialog = true
                                            "
                                        >
                                            <v-icon class="mr-2"
                                                >mdi-account-off</v-icon
                                            >
                                            Make Inactive
                                        </v-btn>
                                    </v-card-text>
                                </v-card>
                            </template>
                        </div>
                    </v-tab-item>
                    <v-tab-item key="tab-program" value="tab-program">
                        <program-admin
                            :administrator="administrator"
                            :included="included"
                        ></program-admin>
                    </v-tab-item>
                </v-tabs-items>
            </div>
        </v-slide-x-reverse-transition>
        <v-tooltip top v-if="isAdding && inviting">
            <template v-slot:activator="{ attr, on }">
                <v-btn
                    fab
                    color="secondary"
                    @click="add"
                    v-bind="attr"
                    v-on="on"
                >
                    <v-icon>mdi-plus</v-icon>
                </v-btn>
            </template>
            <span>Add Additional Instructor</span>
        </v-tooltip>
        <v-dialog v-model="inactivateDialog" max-width="800">
            <instructor-inactive
                v-if="inactivateDialog"
                :program="program"
                :instructorData="$_.get(instructorsData, '[0].modelData', {})"
                :currentSession="currentSession"
                @close="inactivateDialog = false"
                @make-inactive="inactivateInstructor"
            ></instructor-inactive>
        </v-dialog>
    </psi-form-layout-section>
</template>
<script>
const MODE_EDITING = 0,
    MODE_ADDING = 1;
export default {
    name: "program-instructors",
    components: {
        FillMockNames: () => import("../FillMockNames.vue"),
        ProgramAdmin: () => import("../ProgramEdit/ProgramAdmin.vue"),
        InstructorInactive: () => import("./InstructorInactive.vue"),
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        program: {
            type: Object,
            required: true,
        },
        instructors: {
            type: Array,
            required: true,
        },
        sessions: {
            type: Array,
            required: true,
        },
        sectionLoading: {
            type: Object,
            required: false,
            default: () => {
                return {
                    instructors: false,
                };
            },
        },
        query: {
            type: Object,
            required: false,
            default: () => {
                return {};
            },
        },
    },
    data() {
        return {
            tab: "tab-invitation",
            inactivateDialog: false,
            env: process.env.NODE_ENV,
            toggle:
                this.instructors.length > 0 &&
                !this.$_.get(this.query, "addinstructors", false)
                    ? MODE_EDITING
                    : MODE_ADDING,
            instructorsData: [],
            section: {
                ref: "program-instructors",
                name: "update-program-instructors",
            },
            fields: [
                {
                    type: "text",
                    name: "name",
                    key: "name",
                    label: "Name",
                    icon: this.$config("icons.program"),
                    required: true,
                },
                {
                    type: "text",
                    format: "email",
                    name: "email",
                    key: "email",
                    label: "Email",
                    icon: this.$config("icons.email"),
                    required: true,
                },
                {
                    type: "text",
                    format: "tel",
                    name: "phone",
                    key: "phone",
                    label: "Phone",
                    icon: this.$config("icons.phone"),
                    required: false,
                },
            ],
        };
    },
    computed: {
        currentSession() {
            return this.sessions.find((session) => session.is_current) ?? {};
        },
        disabled() {
            return (
                this.$_.get(this.program, "id", 0) === 0 ||
                this.instructorCount === 0 ||
                (this.tab === "tab-program" && this.included)
            );
        },
        included() {
            return this.instructors.length > 0
                ? this.instructors.find(
                      (ins) =>
                          ins.id === this.$_.get(this.administrator, "id", "")
                  ) !== undefined
                : false;
        },
        shouldMock() {
            return (
                ["development", "staging"].includes(this.env) && this.isAdding
            );
        },
        instructorCount() {
            return this.instructorsData.length;
        },
        administrator() {
            return this.$_.get(this.program, "administrator", {});
        },
        label() {
            const type =
                this.tab === "tab-invitation"
                    ? this.instructorLabel
                    : "Administrator";
            const mode = this.tab === "tab-invitation" ? this.mode : "Include";

            return `${mode} ${type}`;
        },
        instructorLabel() {
            return this.instructorsData.length > 1
                ? "Instructors"
                : "Instructor";
        },
        mode() {
            return this.toggle === MODE_EDITING ? "Update" : "Add";
        },
        isEditing() {
            return this.toggle === MODE_EDITING;
        },
        isAdding() {
            return this.toggle === MODE_ADDING;
        },
        inviting() {
            return this.tab === "tab-invitation";
        },
    },
    watch: {
        toggle: {
            immediate: true,
            handler(toggle) {
                if (toggle === MODE_ADDING) {
                    this.instructorsData.splice(0);
                    this.add();
                }
                // resetting on toggle
                if (toggle === MODE_EDITING) {
                    this.instructorsData.splice(0);
                }
            },
        },
    },
    methods: {
        fillInstructors(names) {
            this.instructorsData = this.instructorsData.map((data, index) => {
                return {
                    ...data,
                    modelData: {
                        ...data.modelData,
                        name: names[index].name,
                        email: names[index].email,
                    },
                };
            });
        },
        update() {},
        remove(id) {
            const index = this.instructorsData.find(
                (item) => item.modelData.id === id
            );
            this.instructorsData.splice(index, 1);
        },
        add() {
            const modelData = this.initialize();
            const index = this.instructorsData.length;
            this.instructorsData.splice(index, 0, {
                fields: this.fields,
                section: {
                    ref: `${this.section.ref}_${index}`,
                    name: `${this.section.name}_${index}`,
                },
                modelData,
            });
        },
        initialize() {
            let modelData = {
                id: this.$uuid.v4(),
            };
            this.fields.forEach((field) => (modelData[field.key] = ""));
            return modelData;
        },
        selectInstructor(instructor) {
            this.instructorsData.splice(0, this.instructorsData.length, {
                fields: this.fields,
                section: {
                    ref: `${this.section.ref}`,
                    name: `${this.section.name}`,
                },
                modelData: instructor,
            });
        },
        inactivateInstructor(instructorId) {
            this.$emit("remove", this.id, {
                id: this.program.id,
                instructorId,
            });
        },
        save() {
            // are we adding the administrator?
            if (this.tab === "tab-program") {
                this.$emit("update", this.id, {
                    id: this.program.id,
                    data: {
                        meta: {
                            mode: this.mode,
                        },
                        instructors: [
                            {
                                id: this.administrator.id,
                            },
                        ],
                    },
                });
            } else {
                let valid = true;
                this.instructorsData.forEach((item) => {
                    if (!this.$refs[item.section.ref][0].validate()) {
                        valid = false;
                    }
                });
                if (valid) {
                    if (this.isAdding) {
                        const payload = {
                            id: this.program.id,
                            data: {
                                meta: {
                                    mode: this.mode,
                                },
                                instructors: this.instructorsData.map(
                                    (item) => {
                                        const {
                                            modelData: { name, email, phone },
                                        } = item;
                                        return {
                                            name,
                                            email,
                                            phone,
                                        };
                                    }
                                ),
                            },
                        };
                        this.$emit("update", this.id, payload);
                    }
                    if (this.isEditing) {
                        const payload = {
                            id: this.program.id,
                            data: {
                                meta: {
                                    mode: this.mode,
                                },
                                ...this.$_.pick(
                                    this.instructorsData[0].modelData,
                                    ["id", "name", "email", "phone"]
                                ),
                            },
                        };
                        this.$emit("update", this.id, payload);
                    }
                }
            }
        },
    },
    mounted() {
        if (this.instructors.length === 0) {
            this.add();
        }
        this.$emit("mounted", this.id);
    },
};
</script>

<style scoped>
</style>